import { TrendingUp } from "lucide-react";
import { useState } from "react";
import { useListContext, useNotify, useRefresh, useUpdate } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { Status } from "../misc/Status";
import type { Contact } from "../types";

/**
 * Bulk-set Status on the selected contacts — same "select from a list, apply
 * to every selected id" shape as BulkTagButton, but a plain overwrite
 * (status is single-value) rather than an additive array merge.
 */
export function BulkStatusButton() {
  const notify = useNotify();
  const refresh = useRefresh();
  const { noteStatuses } = useConfigurationContext();
  const [update] = useUpdate<Contact>("contacts", undefined, {
    returnPromise: true,
  });
  const { onUnselectItems, selectedIds = [] } = useListContext<Contact>();
  const [open, setOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  if (!selectedIds.length) return null;

  const applyStatus = async (status: string) => {
    setIsApplying(true);
    try {
      await Promise.all(
        selectedIds.map((id) =>
          update("contacts", {
            id,
            data: { status },
            previousData: { id } as Contact,
          }),
        ),
      );
      notify("crm.common.bulk_update_success", {
        type: "success",
        messageArgs: { _: `Status updated for ${selectedIds.length} contacts` },
      });
      setOpen(false);
      onUnselectItems();
      refresh();
    } catch (error) {
      notify("crm.common.bulk_update_error", {
        type: "error",
        messageArgs: { _: "Failed to update status" },
      });
      console.error("Bulk status update failed:", error);
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9"
        onClick={() => setOpen(true)}
      >
        <TrendingUp />
        Status
      </Button>

      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && setOpen(false)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Set Status</DialogTitle>
            <DialogDescription>
              Applies to all {selectedIds.length} selected contacts.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col space-y-2 items-start">
            {noteStatuses.map((status) => (
              <Button
                key={status.value}
                type="button"
                variant="ghost"
                disabled={isApplying}
                className="px-0 py-0 hover:bg-default dark:hover:bg-default mb-0"
                onClick={() => applyStatus(status.value)}
              >
                <span className="flex items-center gap-2">
                  {status.label} <Status status={status.value} />
                </span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
